import { useEffect, useState } from "react";
import type { ItemType } from "@/context/JourneyContext";
import { TYPE_ICON } from "@/lib/itinerary-display";
import { Compass } from "lucide-react";

interface PlacePhotoProps {
  googlePlaceId?: string;
  // Used when there is no googlePlaceId: the area or the destination, so
  // the card still shows something.
  photoPlaceId?: string;
  // Varies which of the place's photos is shown, so stops sharing one
  // fallback place do not repeat the same image.
  photoIndex?: number;
  // Called when Google does not know the place id at all, which means the
  // stop is carrying a bad one: it has no photo AND its Maps link cannot
  // open a listing. The parent clears it so the stop is looked up again.
  onInvalidPlace?: () => void;
  type: ItemType;
  // "thumb" = the stop card's flush square: 62px until xl, then the
  // artboard's 150px full-height column. Below xl the planner's three
  // columns leave the card under 300px, which a 150px photo would eat. "banner" = full-bleed hero strip.
  // Both are square-cornered and sit flush to the card edge, per the
  // artboard; the fill behind them is --fn-map, not an accent tint.
  variant: "thumb" | "banner";
  alt?: string;
}

// Renders a real Google Places photo for an item, falling back to a
// tinted type-icon tile when there's no place id or the photo fails.
//
// /api/place-photo 302-redirects to the actual Google image URL, so we
// can use it directly as an <img src> — the browser follows the
// redirect and caches the image. We track load errors to swap in the
// fallback, and reset state if the place id changes.
const PlacePhoto = ({
  googlePlaceId,
  photoPlaceId,
  photoIndex = 0,
  onInvalidPlace,
  type,
  variant,
  alt,
}: PlacePhotoProps) => {
  const [failed, setFailed] = useState(false);
  const placeId = googlePlaceId || photoPlaceId;
  useEffect(() => {
    setFailed(false);
  }, [placeId]);

  const Icon = TYPE_ICON[type] ?? Compass;
  const showPhoto = !!placeId && !failed;
  // The index only varies for a stand-in place; a stop's own listing shows
  // its first photo, which is the one Google considers most representative.
  const index = googlePlaceId ? 0 : photoIndex % 10;
  const src = placeId
    ? `/api/place-photo?placeId=${encodeURIComponent(placeId)}${index ? `&index=${index}` : ""}`
    : "";

  const sizeClasses =
    variant === "thumb"
      ? "h-[62px] w-[62px] xl:h-auto xl:w-[150px] xl:min-h-[150px] xl:self-stretch"
      : "w-full h-[130px] lg:h-[190px]";

  if (!showPhoto) {
    return (
      <div
        className={`${sizeClasses} shrink-0 bg-map flex items-center justify-center`}
        aria-hidden="true"
      >
        <Icon
          className={`text-ink-faint ${
            variant === "thumb" ? "h-5 w-5" : "h-8 w-8"
          }`}
        />
      </div>
    );
  }

  return (
    <div className={`${sizeClasses} shrink-0 overflow-hidden bg-map`}>
      <img
        src={src}
        alt={alt || ""}
        loading="lazy"
        className="h-full w-full object-cover"
        onError={() => {
          setFailed(true);
          // An <img> error carries no status, so ask why. A place Google
          // does not have is worth repairing; a place with no photo is
          // simply a place with no photo.
          if (!onInvalidPlace || !placeId) return;
          fetch(src)
            .then((res) => (res.status === 404 ? res.json().catch(() => null) : null))
            .then((body) => {
              if (body?.code === "invalid_place") onInvalidPlace();
            })
            .catch(() => {});
        }}
      />
    </div>
  );
};

export default PlacePhoto;
